import React, { useEffect, useState } from 'react';
import { BookMarked, ShieldCheck, ShieldAlert, ShieldQuestion } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useCanEdit, useDeck } from './store';
import { LIBRARY, collectSources, libText, shortCite, trust, TRUST_LABEL, Trust, withConsultDate } from './sources';
import { CitationBuilder } from './CitationBuilder';

const ICON: Record<Trust, any> = { empty: ShieldAlert, trusted: ShieldCheck, check: ShieldQuestion, ref: ShieldQuestion };
const TONE: Record<Trust, string> = { empty: 'bg-red-600 text-white', trusted: 'bg-emerald-600 text-white', check: 'bg-amber-500 text-black', ref: 'bg-amber-200 text-black' };

export function TrustBadge({ src, testId }: { src: string; testId?: string }) {
  const t = trust(src);
  const I = ICON[t];
  return (
    <span data-testid={testId} title={TRUST_LABEL[t]} className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-[2px] font-ui text-[11px] font-semibold ${TONE[t]}`}>
      <I size={11} /> {TRUST_LABEL[t]}
    </span>
  );
}

export function LibraryPicker({ onPick, testId }: { onPick: (t: string) => void; testId: string }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={testId} className="inline-flex shrink-0 items-center gap-1 rounded-full bg-neutral-900 px-2 py-[2px] font-ui text-[11px] font-semibold text-white hover:bg-blue-600 transition-colors">
          <BookMarked size={11} /> Sources sûres
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[460px] max-h-[420px] overflow-auto p-2 font-ui" align="start">
        <p className="px-2 pb-2 text-[11px] text-neutral-500">Points de départ officiels ou académiques. Ouvrez le lien, vérifiez la page exacte et ajoutez la date de consultation.</p>
        {LIBRARY.map((s) => (
          <button key={s.id} data-testid={`library-pick-${s.id}`} onClick={() => onPick(libText(s))} className="block w-full rounded-md px-2 py-1.5 text-left text-[12px] hover:bg-neutral-100">
            <span className="mr-1 rounded bg-neutral-200 px-1 text-[10px] uppercase">{s.cat}</span>
            {s.citation}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

interface Props { k: string; parentKey?: string; testId: string; label?: string; dark?: boolean; className?: string; size?: number }

export function SourceLine({ k, parentKey, testId, label = 'Source', dark, className = '', size = 13 }: Props) {
  const { get, set, data } = useDeck();
  const canEdit = useCanEdit();
  const src: string = get(k) || '';
  const parent = parentKey ? get(parentKey) : 'x';
  const parentFilled = !!(typeof parent === 'string' ? parent.trim() : parent);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(src);
  useEffect(() => { if (!open) setDraft(src); }, [src, open]);
  if (!canEdit && (!src.trim() || !parentFilled)) return null;
  const missing = !src.trim() && parentFilled;
  const num = collectSources(data).findIndex((s) => s.text === src.trim()) + 1;
  const commit = (t: string) => { setDraft(t); set(k, t); };
  const tone = dark ? 'text-white/80' : 'text-black/75';

  const line = (
    <span className={`line-clamp-2 min-w-0 flex-1 text-left font-type leading-snug ${tone}`} style={{ fontSize: size }} data-testid={`${testId}-text`}>
      {src.trim() ? <>{num > 0 && <span className="mr-1 text-blue-600">[{num}]</span>}{shortCite(src)}</> : <span className="opacity-40">Ajouter une source (auteur, titre, date, lien)</span>}
    </span>
  );

  return (
    <div className={`flex min-w-0 items-start gap-2 ${className}`} data-testid={testId}>
      <span className={`shrink-0 pt-[1px] font-ui font-semibold uppercase tracking-wider ${dark ? 'text-white/60' : 'text-black/55'}`} style={{ fontSize: size - 2 }}>{label} :</span>
      {!canEdit ? line : (
        <Popover open={open} onOpenChange={(o) => { if (!o) commit(withConsultDate(draft.trim())); setOpen(o); }}>
          <PopoverTrigger asChild>
            <button data-testid={`${testId}-input`} className={`flex min-w-0 flex-1 items-start gap-2 rounded px-1 outline-1 outline-dashed outline-transparent hover:outline-neutral-400 transition-[outline-color] ${missing ? '!outline-red-500' : ''}`}>
              {line}
              {(src.trim() || missing) && <TrustBadge src={src} testId={`${testId}-trust`} />}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[480px] p-3 font-ui" align="start" data-testid={`${testId}-editor`} onKeyDown={(e) => e.stopPropagation()}>
            <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-neutral-500">Citation complète (affichée en entier sur la slide Sources)</p>
            <textarea
              data-testid={`${testId}-textarea`}
              value={draft}
              onChange={(e) => commit(e.target.value)}
              onBlur={() => commit(withConsultDate(draft.trim()))}
              rows={4}
              placeholder="NOM, Prénom ou ORGANISME. Titre [en ligne]. Éditeur, date. [Consulté le …]. Disponible à l'adresse : https://…"
              className="w-full resize-none rounded-md border border-neutral-300 p-2 font-type text-[13px] leading-snug outline-none focus:border-blue-600"
            />
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <TrustBadge src={draft} />
              <span className="ml-auto" />
              <LibraryPicker onPick={commit} testId={`${testId}-library`} />
              <CitationBuilder onApply={commit} testId={testId} />
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
